import PropTypes from 'prop-types';
import { VBox } from '@/components/VBox';
import { HBox } from '@/components/HBox';

import i18n from '@/i18n';

import { Statistics } from '@/components/Statistics';

import { useSearchParam, useResource } from '@/hooks';
import moment from 'moment';
import { Box, Container, Button, Option, Select, Modal, ModalDialog, ModalClose } from '@mui/joy';
import { ToggleButtonBox } from '@/components/ToggleButtonBox';
import { ToggleButton } from '@/components/ToggleButton';
import { CloudDownload } from '@mui/icons-material';
import { downloadStatistics } from '@/actions/aloader';
import { LoadingPage } from '@/components/LoadingPage';
import { ErrorView } from '@/components/ErrorView';
import { GenericList } from '@/components/GenericList';
import { Typography } from '@mui/material';
import { Img } from '@/components/Img';
import { Link } from 'react-router-dom';
import { ChartLineUp, Funnel } from '@phosphor-icons/react';
import { VictoryPie } from 'victory';
import { useState } from 'react';
import { Case, SwitchCase } from '@/components/Syntax';
import { useMemo } from 'react';
import { useManager } from '@/hooks';

const METRICS = ['streams', 'downloads']

export function TopListContainer({
  start,
  end,
  serviceIds
}) {
  const manager = useManager()

  const [topListViewType, setTopListViewType] = useState('list')

  const { data: top10Recordings, loading: loadingTop10Recordings } = useResource({
    queryKey: ['top', 10, 'recording', 'on', serviceIds],
    path: `/${manager?.type}/${manager?.slug}/top/10/recordings`,
    query: {
      start,
      end,
      serviceIds,
      ordering: 'top',
      limit: 20
    },
    enabled: !!manager
  })

  return (
    <VBox>
      <HBox>
        <Typography sx={{ flex: 1 }} level="h1" component="h1">{i18n.t('top-tracks')}</Typography>
        <ToggleButtonBox> 
          <ToggleButton
            active={topListViewType === 'list'}
            onClick={() => {
              setTopListViewType("list")
            }}
          >
            {i18n.t('list')}
          </ToggleButton>
          <ToggleButton
            active={topListViewType === 'pie'}
            onClick={() => {
              setTopListViewType("pie")
            }}
          >
            {i18n.t('pie-chart')}
          </ToggleButton>
        </ToggleButtonBox>
      </HBox>
      <SwitchCase value={topListViewType}>
        <Case match="list">
          <GenericList
            heading=""
            path={`/top/20/recordings`}
            queryKey={[manager?.type, manager?.slug, 'top', '20', 'recording', start, end]}
            key={`${start}${end}${serviceIds.join(',')}`}
            viewType="list"
            canSearch={false}
            canAdd={false}
            canPaginate={false}
            hasMoreMenu={false}
            showDefaultColumns={false}
            showToolBar={false}
            Icon={ChartLineUp}
            canSelectViewType={false}
            canChangeSortBy={false}
            columns={[
              {
                id: 'number',
                name: i18n.t('number'),
                sm: true,
                type: 'number',
                render: ({ i }) => i 
              },
              {
                id: 'thumbnail_url',
                sm: true,
                name: i18n.t('image'),
                type: 'image',
                render: ({ column, node }) => <Img src={node[column.id]} sx={{ width: '30pt', objectFit: 'contain' }} />
              },
              {
                id: 'name',
                sm: true,
                name: i18n.t('name'),
                type: 'text',
                render: ({ column, node }) => <Link to={`/${manager?.type}/${manager?.slug}/recording/${node.id}`}>{node[column.id]}</Link>
              },
              {
                id: 'totals',
                name: i18n.t('streams'),
                type: 'number',
                render: ({ column, node }) => {
                  const value = node[column.id]
                  if (isNaN(value)) {
                    return <Typography>-</Typography>
                  }
                  return (
                    <Typography sx={{ textAlign: 'right' }}>{value?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
                  )
                } 
              }
            ]}
            query={{
              start,
              end,
              serviceId: serviceIds?.length > 0 ? serviceIds[0] : null,
              ordering: 'top',
              limit: 20
            }}
          />
        </Case>
        <Case match="pie">
          {loadingTop10Recordings ? (
            <LoadingPage />
          ) : (top10Recordings?.results instanceof Array ? (
            <VictoryPie
              colorScale={'qualitative'}
              radius={100}
              style={{
                labels: {
                  fontSize: 5
                }
              }}
              data={
                top10Recordings?.results.map(
                  r => ({
                    x: r.name,
                    y: parseInt(r.totals)
                  })
                )
              }
            />
          ) : <ErrorView />)}
        </Case>
      </SwitchCase>
    </VBox>
  )
}

TopListContainer.propTypes = {
  start: PropTypes.string,
  end: PropTypes.string,
  dataKey: PropTypes.string,
  serviceIds: PropTypes.array
}

export function StatisticsContainer({
  start: defaultStart,
  end: defaultEnd,
  recordingIds,
  artistIds,
  releaseIds,
  showTopList=true
}) {
  const manager = useManager()

  const [end, setEnd] = useSearchParam('toDate', defaultEnd ?? moment().subtract(1, 'day').format('YYYY-MM-DD'))
  const [start, setStart] = useSearchParam('fromDate', defaultStart ?? moment().subtract(8, 'day').format('YYYY-MM-DD'))
  const [serviceId, setServiceId] = useSearchParam('serviceId', '')
  const [showingFiltersDialog, setShowingFiltersDialog] = useState(false)
  
  const [dataKey, setDataKey] = useSearchParam('dataKey', 'streams') ?? 'streams'

  const handleDownloadStatsClick = () => {
    downloadStatistics({
      managerType: manager?.type,
      managerId: manager?.slug,
      query: {
        start,
        end,
        //labelId: label.id,
        recordingIds,
        serviceId,
        artistIds,
        releaseIds
      }
    })
  }

  const period = useMemo(() => {
    let ret = "custom"
    if (
      moment(end).format('YYYY-MM-DD') === moment().subtract(1, 'day').format('YYYY-MM-DD') &&
      moment(start).format('YYYY-MM-DD') === moment().subtract(8, 'day').format('YYYY-MM-DD')
    ) {
      ret = "7-days"
    }
    if (
      moment(end).format('YYYY-MM-DD') === moment().subtract(1, 'day').format('YYYY-MM-DD') &&
      moment(start).format('YYYY-MM-DD') === moment().subtract(29, 'day').format('YYYY-MM-DD')
    ) {
      ret = "28-days"
    }
    return ret
  }, [start, end])

  const setPeriod = (value) => {
    switch (value) {
      case "7-days":
        setEnd(moment().subtract(1, 'day').format('YYYY-MM-DD'))
        setStart(moment().subtract(8, 'day').format('YYYY-MM-DD'))
        break;
      case "28-days":
        setEnd(moment().subtract(1, 'day').format('YYYY-MM-DD'))
        setStart(moment().subtract(29, 'day').format('YYYY-MM-DD'))
        break;
    }
  }
    
  const { data: servicesData } = useResource({
    queryKey: ['services'],
    path: '/services'
  })

  const services = servicesData?.results ?? []

  const filterChildren = (
    <>
      <Select
        sx={{ transitionDelay: `300ms`, minWidth: 200 }}
        placeholder={i18n.t('dsp')}
        defaultValue={serviceId}
        onChange={(event, value) => {
          setServiceId(value)
        }}
      >
        <Option value="">{i18n.t('all-dsps')}</Option>
        {services?.map(service => <Option value={service.slug} key={service.slug}>{service.name}</Option>)}
        
      </Select>
      <Select      
        sx={{ '@media screen and (max-width: 720pt)': { display: 'none' }, transitionDelay: `300ms`, minWidth: 200 }}
        placeholder={i18n.t('period')}
        defaultValue={period}
        onChange={(event, value) => {
          setPeriod(value)
        }}
      >
        <Option value="7-days">{i18n.t('7-days')}</Option>
        <Option value="28-days">{i18n.t('28-days')}</Option>
      </Select>
    </>
  )

  return (
    <VBox>
      <Typography>{start} - {end}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: 1 }}>
        <Box sx={{ '@media screen and (min-width: 720pt)': { display: 'none' },  display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: 1 }}>
          <Button
            startDecorator={<CloudDownload />}
            onClick={handleDownloadStatsClick}
          />
        </Box>
        <Box sx={{ '@media screen and (max-width: 720pt)': { display: 'none', flex: 1 },  display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: 1 }}>
          {filterChildren}
        </Box>
        <Box sx={{ '@media screen and (max-width: 720pt)': { flex: 1 }, alignItems: 'stretch', justifyContent: 'flex-start' }}>
          <ToggleButtonBox>
            {METRICS.map(metric => (
              <ToggleButton
                key={metric}
                active={metric === dataKey}
                onClick={() => {
                  setDataKey(metric)
                }}
              >{i18n.t(metric)}</ToggleButton>
            ))}
          </ToggleButtonBox>
        </Box>
        <Button aria-label="Filter" onClick={() => setShowingFiltersDialog(true)} startDecorator={<Funnel />} sx={{ '@media screen and (min-width: 720pt)': { display: 'none' } }} />
        <Button
          sx={{'@media screen and (max-width: 720pt)': { 'display': 'none' }}}
          startDecorator={<CloudDownload />}
          onClick={handleDownloadStatsClick}
        >
          {i18n.t('download-stats')}
        </Button>
      </Box><br />
      <Select      
        sx={{ '@media screen and (min-width: 720pt)': { display: 'none' }, transitionDelay: `300ms`, minWidth: 200 }}
        placeholder={i18n.t('period')}
        defaultValue={period}
        onChange={(event, value) => {
          setPeriod(value)
        }}
      >
        <Option value="7-days">{i18n.t('7-days')}</Option>
        <Option value="28-days">{i18n.t('28-days')}</Option>
      </Select>
      <StatisticsChartContainer
        period={period}
        start={start}
        end={end}
        dataKey={dataKey}
        serviceIds={[serviceId]}
      />
      {showTopList && <TopListContainer dataKey={dataKey} start={start} end={end} serviceIds={[serviceId]} />}
      <Modal open={showingFiltersDialog} onClose={() => setShowingFiltersDialog(false)}>
        <ModalDialog>
          <ModalClose />
          <VBox>
            {filterChildren}
          </VBox>
        </ModalDialog>
      </Modal>
    </VBox>
  )
}

StatisticsContainer.propTypes = {
  start: PropTypes.string,
  end: PropTypes.string,
  recordingIds: PropTypes.array,
  artistIds: PropTypes.array,
  releaseIds: PropTypes.array,
  showTopList: PropTypes.bool
}

export function StatisticsChartContainer({
  start,
  end,
  dataKey='stream',
  recordingIds,
  serviceIds,
  artistIds,
  releaseIds
}) {
  const manager = useManager()
  console.log("#manager", manager)
  const { data, isLoading } = useResource({
    queryKey: [manager?.type, manager?.slug, 'statistics', 'service', serviceIds?.join(',') ?? '', 'date', start, end, dataKey],
    path: `/${manager?.type}/${manager?.slug}/statistics`,
    query: {
      start,
      end,
      recordingIds,
      serviceIds,
      artistIds,
      releaseIds
    },
    enabled: !!manager
  })

  return (
    <VBox>
      <HBox>
        {isLoading ?
          <LoadingPage />
            : data ? (
          <Statistics
            key={dataKey}
            dataKey={dataKey}
            start={start}
            end={end}
            data={data }
          />
        ) : <ErrorView />}
      </HBox>
    </VBox>
  )
}

StatisticsChartContainer.propTypes = {
  start: PropTypes.string,
  end: PropTypes.string,
  dataKey: PropTypes.string,
  recordingIds: PropTypes.array,
  serviceIds: PropTypes.array,
  artistIds: PropTypes.array,
  releaseIds: PropTypes.array
}

export default function StatisticsPage() {
  return (
    <Container>
      <VBox>
        <h1>{i18n.t('statistics')}</h1>
        <StatisticsContainer />
      </VBox>
    </Container>
  )
}
